import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { dispose, initialize, removeCashoutBet, selectBets, selectIsLoading } from './open-bets-duck';
import { uniqueId } from '@comeon/utility';
import { selectIsBettingServiceApiReady, selectIsSBApiReady } from '../../api/sportsbook-api-duck';
import { selectIsConfigurationInitialized } from '../../sportsbook-configuration-duck';
import { selectIsAuthenticated } from '../../sportsbook-root-selectors';
import { AppDispatch } from '~/store/types';

/**
 * Provides open bets
 *
 * Hook will ensure we only subscribe to open bets once for the application
 * Hook is fairly low cost and is intended to be reused wherever needed
 */
const useOpenBets = () => {
	const dispatch: AppDispatch = useDispatch();
	const ref = useMemo(uniqueId, []);
	const isLoading = useSelector(selectIsLoading);
	const bets = useSelector(selectBets);
	// Just safeguards
	const isSBApiReady = useSelector(selectIsSBApiReady);
	const isBettingServiceApiReady = useSelector(selectIsBettingServiceApiReady);
	const isConfigurationInitialized = useSelector(selectIsConfigurationInitialized);
	const isAuthenticated = useSelector(selectIsAuthenticated);
	useEffect(() => {
		if (!isAuthenticated || !isConfigurationInitialized || !isBettingServiceApiReady || !isSBApiReady) {
			return;
		}
		dispatch(initialize(ref));
		return () => {
			dispatch(dispose(ref));
		};
	}, [ref, isAuthenticated, isConfigurationInitialized, isBettingServiceApiReady, isSBApiReady, dispatch]);

	const removeCashoutBetCallback = useCallback((betId: number) => dispatch(removeCashoutBet(betId)), [dispatch]);

	return {
		isLoading,
		bets,
		betsCount: bets.length,
		removeCashoutBet: removeCashoutBetCallback
	};
};

export default useOpenBets;
